/**
 * Individual Billing Management Page
 * Epic 86: Individual Subscription Management
 *
 * Displays subscription status, usage metrics, and billing controls
 * for individual adult learners
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Tile, Button, Tag, ProgressBar, Modal, TextArea, RadioButtonGroup, RadioButton, Loading } from '@carbon/react';
import { Renew, ChevronRight } from '@carbon/icons-react';
import './IndividualBilling.scss';

interface Subscription {
  id: string;
  plan_name: string;
  status: string;
  billing_interval: string;
  price: number;
  currency: string;
  current_period_end: number;
  trial_end?: number;
  cancel_at_period_end: boolean;
  features: string[];
}

interface Usage {
  ai_tokens_used: number;
  ai_tokens_limit: number;
  period_end: number;
}

interface SubscriptionData {
  subscription: Subscription | null;
  usage: Usage | null;
}

// Module-level cache to prevent duplicate fetches across remounts
let dataCache: SubscriptionData | null = null;
let isFetching = false;
let lastFetchTime = 0;
const CACHE_DURATION = 60000; // 1 minute
let mountCounter = 0;

const IndividualBillingComponent: React.FC = () => {
  const componentId = React.useRef(++mountCounter);
  const [subscription, setSubscription] = useState<Subscription | null>(dataCache?.subscription || null);
  const [usage, setUsage] = useState<Usage | null>(dataCache?.usage || null);
  const [loading, setLoading] = useState(!dataCache);
  const [cancelling, setCancelling] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelImmediate, setCancelImmediate] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Track mount/unmount lifecycle
  React.useEffect(() => {
    console.log(`[IndividualBilling #${componentId.current}] MOUNTED`);
    return () => {
      console.log(`[IndividualBilling #${componentId.current}] UNMOUNTED`);
    };
  }, []);

  const fetchSubscription = useCallback(async () => {
    console.log(`[IndividualBilling #${componentId.current}] fetchSubscription called`);
    const now = Date.now();

    // Use cached data if available and fresh
    if (dataCache && now - lastFetchTime < CACHE_DURATION) {
      console.log('[IndividualBilling] Using cached data');
      setSubscription(dataCache.subscription);
      setUsage(dataCache.usage);
      setLoading(false);
      return;
    }

    // If another instance is fetching, wait for it
    if (isFetching) {
      console.log('[IndividualBilling] Fetch already in progress, waiting...');
      const startWait = Date.now();
      while (isFetching && Date.now() - startWait < 5000) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      // Use cached result if available, or stop loading if cache is still null
      if (dataCache) {
        console.log('[IndividualBilling] Using data from concurrent fetch');
        setSubscription(dataCache.subscription);
        setUsage(dataCache.usage);
      } else {
        console.warn('[IndividualBilling] Concurrent fetch timed out or failed, stopping loading');
      }
      setLoading(false);
      return;
    }

    isFetching = true;
    console.log('[IndividualBilling] Starting API fetch, isFetching =', isFetching);

    try {
      // Use same token retrieval pattern as BillingDashboard
      const sessionToken = sessionStorage.getItem('access_token') || sessionStorage.getItem('sigma08_jwt_token');
      const localToken = localStorage.getItem('access_token') || localStorage.getItem('sigma08_jwt_token');
      const token = sessionToken || localToken;

      console.log('[IndividualBilling] Token check:', {
        hasSessionToken: !!sessionToken,
        hasLocalToken: !!localToken,
        hasToken: !!token,
        tokenLength: token?.length,
      });

      if (!token) {
        console.error('[IndividualBilling] No authentication token found');
        setLoading(false);
        isFetching = false;
        return;
      }

      // Use relative URL instead of hardcoded staging URL
      console.log('[IndividualBilling] Fetching from /api/v1/subscriptions/individual/current');
      const response = await fetch('/api/v1/subscriptions/individual/current', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('[IndividualBilling] API response:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[IndividualBilling] API error:', errorText);
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('[IndividualBilling] Data received:', data);

      if (data.has_subscription) {
        console.log('[IndividualBilling] User has subscription, updating state');
        setSubscription(data.subscription);
        setUsage(data.usage);
        dataCache = { subscription: data.subscription, usage: data.usage };
        lastFetchTime = Date.now();
      } else {
        console.log('[IndividualBilling] No subscription, setting null state');
        setSubscription(null);
        setUsage(null);
        dataCache = { subscription: null, usage: null };
        lastFetchTime = Date.now();
      }
    } catch (error) {
      console.error('[IndividualBilling] Error fetching subscription:', error);
      console.error('[IndividualBilling] Error stack:', error instanceof Error ? error.stack : 'No stack');
      setSubscription(null);
      setUsage(null);
      dataCache = { subscription: null, usage: null };
      lastFetchTime = Date.now();
    } finally {
      console.log('[IndividualBilling] FINALLY block executing - setting loading to false, isFetching to false');
      setLoading(false);
      isFetching = false;
      console.log('[IndividualBilling] FINALLY block complete - loading should be false now');
    }
  }, []);

  useEffect(() => {
    fetchSubscription();

    // Safety timeout: force loading to false after 10 seconds
    const safetyTimeout = setTimeout(() => {
      console.warn('[IndividualBilling] Safety timeout triggered - forcing loading to false');
      setLoading(false);
    }, 10000);

    return () => clearTimeout(safetyTimeout);
  }, [fetchSubscription]);

  const openBillingPortal = async () => {
    try {
      const sessionToken = sessionStorage.getItem('access_token') || sessionStorage.getItem('sigma08_jwt_token');
      const localToken = localStorage.getItem('access_token') || localStorage.getItem('sigma08_jwt_token');
      const token = sessionToken || localToken;

      const response = await fetch('/api/v1/subscriptions/individual/portal', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        window.location.href = data.portal_url;
      }
    } catch (error) {
      console.error('Failed to open billing portal:', error);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);

    try {
      const sessionToken = sessionStorage.getItem('access_token') || sessionStorage.getItem('sigma08_jwt_token');
      const localToken = localStorage.getItem('access_token') || localStorage.getItem('sigma08_jwt_token');
      const token = sessionToken || localToken;

      const response = await fetch('/api/v1/subscriptions/individual', {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          immediate: cancelImmediate,
          reason: cancelReason,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setCancelModalOpen(false);
        fetchSubscription(); // Refresh subscription data
      }
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return <Loading description="Loading subscription..." withOverlay={false} />;
  }

  if (!subscription) {
    return (
      <div className="individual-billing">
        <Tile className="no-subscription-tile">
          <h2 className="cds--type-heading-04">No Active Subscription</h2>
          <p>Subscribe to unlock AI tutoring, certifications, and professional development features.</p>
          <Button kind="primary" renderIcon={Renew} onClick={() => (window.location.href = '/pricing')}>
            View Plans
          </Button>
        </Tile>
      </div>
    );
  }

  const statusTagType =
    subscription.status === 'active'
      ? 'green'
      : subscription.status === 'trialing'
        ? 'blue'
        : subscription.status === 'past_due'
          ? 'red'
          : 'gray';

  const nextBillingDate = new Date(subscription.current_period_end * 1000).toLocaleDateString();
  const usagePercentage = usage ? (usage.ai_tokens_used / usage.ai_tokens_limit) * 100 : 0;

  return (
    <div className="individual-billing">
      <h1 className="cds--type-heading-05">Subscription & Billing</h1>

      {/* Subscription Status Card */}
      <Tile className="subscription-status-tile">
        <div className="subscription-header">
          <div>
            <h3 className="cds--type-heading-02">{subscription.plan_name}</h3>
            <Tag type={statusTagType}>{subscription.status.toUpperCase()}</Tag>
          </div>
          <div className="subscription-price">
            ${subscription.price}/{subscription.billing_interval}
          </div>
        </div>

        {subscription.trial_end && (
          <div className="trial-info">Trial ends: {new Date(subscription.trial_end * 1000).toLocaleDateString()}</div>
        )}

        <div className="billing-info">
          <p>Next billing date: {nextBillingDate}</p>
          {subscription.cancel_at_period_end && (
            <p className="cancellation-notice">⚠️ Subscription will cancel on {nextBillingDate}</p>
          )}
        </div>

        <div className="subscription-actions">
          <Button kind="primary" onClick={openBillingPortal}>
            Manage Billing
          </Button>
          {!subscription.cancel_at_period_end && (
            <Button kind="tertiary" onClick={() => setCancelModalOpen(true)}>
              Cancel Subscription
            </Button>
          )}
        </div>
      </Tile>

      {/* Usage Metrics Card */}
      {usage && (
        <Tile className="usage-metrics-tile">
          <h3 className="cds--type-heading-02">AI Usage This Month</h3>
          <div className="usage-progress">
            <ProgressBar
              label="AI Tokens Used"
              value={usagePercentage}
              max={100}
              helperText={`${usage.ai_tokens_used.toLocaleString()} / ${usage.ai_tokens_limit.toLocaleString()} tokens`}
            />
          </div>
          <p className="usage-reset">Resets on {new Date(usage.period_end * 1000).toLocaleDateString()}</p>
        </Tile>
      )}

      {/* Features Card */}
      <Tile className="features-tile">
        <h3 className="cds--type-heading-02">Your Plan Includes</h3>
        <ul className="features-list">
          {subscription.features.map((feature, index) => (
            <li key={index}>
              <ChevronRight size={16} /> {feature}
            </li>
          ))}
        </ul>
      </Tile>

      {/* Cancellation Modal */}
      <Modal
        open={cancelModalOpen}
        modalHeading="Cancel Subscription"
        primaryButtonText={cancelling ? 'Cancelling...' : 'Confirm Cancellation'}
        secondaryButtonText="Keep Subscription"
        onRequestClose={() => setCancelModalOpen(false)}
        onRequestSubmit={handleCancelSubscription}
        danger
        primaryButtonDisabled={cancelling}
      >
        <p>Are you sure you want to cancel your subscription? You'll lose access to:</p>
        <ul>
          <li>AI tutoring and coaching</li>
          <li>Professional certifications</li>
          <li>Progress tracking and analytics</li>
          <li>Career advancement tools</li>
        </ul>

        <RadioButtonGroup
          legendText="When should we cancel?"
          name="cancel-timing"
          valueSelected={cancelImmediate ? 'immediate' : 'end_of_period'}
          onChange={value => setCancelImmediate(value === 'immediate')}
        >
          <RadioButton labelText="At end of billing period (you keep access)" value="end_of_period" id="cancel-end" />
          <RadioButton labelText="Immediately (lose access now)" value="immediate" id="cancel-immediate" />
        </RadioButtonGroup>

        <TextArea
          labelText="Why are you canceling? (optional)"
          placeholder="Help us improve..."
          value={cancelReason}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCancelReason(e.target.value)}
          rows={3}
        />
      </Modal>
    </div>
  );
};

// Wrap in React.memo to prevent unnecessary remounts
// Applies same fix as BillingDashboard for HeaderContainer render prop issue
export const IndividualBilling = React.memo(IndividualBillingComponent);

export default IndividualBilling;
